import * as React from "react";
import Image from "next/image";
import {
  ArrowRight,
  BriefcaseBusiness,
  Coffee,
  Copy,
  Download,
  Mail,
  MessageSquareText,
  Play,
  type LucideIcon,
} from "lucide-react";

import {
  Stage,
  StageFacts,
  StageHeadline,
  StageLead,
} from "@/components/site/sections/six-pillars-preview/stage";
import { MANUAL_FUNCTION_TOKEN_META } from "@/lib/manual/manual-function-tokens";
import {
  getSixPillarsPreviewPerson,
  sixPillarsRandomCoffeePreview,
  type RandomCoffeePreviewDeck,
  type RandomCoffeePreviewInvite,
} from "@/lib/six-pillars-preview";
import { formatShortDate, formatWeekRange } from "@/lib/six-pillars-stage";
import { cn } from "@/lib/utils";

// The Random Coffee page after the product's redesign (cooperly_app
// features/network/network-stage.tsx, profile-card-fan.tsx, start-topics.tsx and
// history-row.tsx), seen by the team lead: this week's partner on the dark stage
// with their 30-second cards, then where to start and the previous coffees.

type FunctionCode = keyof typeof MANUAL_FUNCTION_TOKEN_META;

const FUNCTIONS: Record<FunctionCode, { label: string; onDark: string }> = {
  R: { label: "Reasoning", onDark: "#8EA6F2" },
  Ca: { label: "Capacity", onDark: "#6CC29F" },
  C: { label: "Connection", onDark: "#F29A66" },
  D: { label: "Drive", onDark: "#C9A27A" },
};

const STAGE_LINK =
  "inline-flex min-h-6 items-center gap-2 text-sm text-white/80 underline decoration-white/25 underline-offset-[3px] transition-colors hover:text-white hover:decoration-white/70 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:outline-none";
const STAGE_BUTTON =
  "inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-white/25 px-4 text-sm font-semibold text-white transition-colors hover:border-white/40 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:outline-none max-sm:w-full";

function profileFunctions(name: string): FunctionCode[] {
  return (getSixPillarsPreviewPerson(name)?.profileAxes ?? []).filter(
    (code): code is FunctionCode => code in FUNCTIONS,
  );
}

export function RandomCoffeePreview({
  variant,
}: {
  variant: "compact" | "expanded";
}) {
  const compact = variant === "compact";
  const { current, history, currentDeck } = sixPillarsRandomCoffeePreview;

  return (
    <div className={cn("px-4 py-4 sm:px-5", compact ? "sm:py-5" : "sm:px-6 sm:py-6")}>
      <div className="flex flex-col gap-10">
        <RandomCoffeeStage invite={current} deck={currentDeck} />
        <StartTopics
          guide={current.conversationGuide}
          workTopics={current.workTopics}
          lifestyleTopics={current.lifestyleTopics}
        />
        <PreviousCoffees invites={history} />
      </div>
    </div>
  );
}

function RandomCoffeeStage({
  invite,
  deck,
}: {
  invite: RandomCoffeePreviewInvite;
  deck: RandomCoffeePreviewDeck;
}) {
  const headingId = React.useId();
  const [firstName, ...rest] = invite.partnerName.split(" ");
  const functions = profileFunctions(invite.partnerName);

  return (
    <Stage
      labelledBy={headingId}
      asideFirst
      asideClassName="lg:flex-[0_1_400px]"
      aside={
        <>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -top-28 -right-8 -z-10 text-[400px] leading-none text-white/[0.035] select-none max-sm:-top-12 max-sm:text-[260px]"
          >
            {deck.glyph}
          </span>
          <ProfileCardFan
            invite={invite}
            deck={deck}
            label={`${firstName} in 30 seconds`}
            ring={
              <PartnerRing
                name={invite.partnerName}
                functions={functions}
                className="size-full"
              />
            }
          />
        </>
      }
    >
      <StageHeadline id={headingId} value={firstName} caption={rest.join(" ")} />

      <p className="m-0 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[15px] leading-snug text-white/75">
        <span className="min-w-0">
          {invite.partnerRole}
          <span aria-hidden="true" className="ml-2.5 text-white/35">
            ·
          </span>
        </span>
        <span className="min-w-0">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-flex size-[18px] items-center justify-center rounded-[5px] bg-[#F5C84C] text-[10px] font-semibold text-zinc-950"
            >
              {invite.teamName.slice(0, 1)}
            </span>
            {invite.teamName}
          </span>
          <span aria-hidden="true" className="ml-2.5 text-white/35">
            ·
          </span>
        </span>
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <Mail className="size-[15px] flex-none text-white/45" aria-hidden="true" />
          <span className="min-w-0 [overflow-wrap:anywhere] text-white/85 underline decoration-white/25 underline-offset-[3px]">
            {invite.partnerEmail}
          </span>
          <span
            aria-hidden="true"
            className="-ml-0.5 inline-flex size-7 items-center justify-center rounded-md text-zinc-500"
          >
            <Copy className="size-3.5" />
          </span>
        </span>
      </p>

      <StageLead>{invite.reason}</StageLead>

      <StageFacts
        kind="text"
        items={[
          { key: "week", label: "Week", value: formatWeekRange(invite.weekStart) },
          { key: "format", label: "Format", value: invite.format },
          {
            key: "previous",
            label: "Last coffee",
            value: <span className="text-[#9DB2F5]">First meeting</span>,
          },
        ]}
      />

      <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6">
        <button type="button" className={STAGE_BUTTON}>
          <MessageSquareText className="size-4" aria-hidden="true" />
          How to run this 1:1
        </button>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <button type="button" className={STAGE_LINK}>
            Open profile
          </button>
          <button type="button" className={STAGE_LINK}>
            <Download className="size-[15px] flex-none text-white/50" aria-hidden="true" />
            Full profile, PDF
          </button>
        </div>
      </div>
    </Stage>
  );
}

/** Back to front: the dark Don't card, the Do card, the cover on top. Offsets are in card widths. */
const FAN: Array<{ face: ProfileCardFace; left: number; top: number; rotate: number }> = [
  { face: "dont", left: 0.62, top: 0.16, rotate: 7 },
  { face: "do", left: 0.31, top: 0.06, rotate: 3 },
  { face: "cover", left: 0, top: 0, rotate: -3 },
];

function ProfileCardFan({
  invite,
  deck,
  label,
  ring,
}: {
  invite: RandomCoffeePreviewInvite;
  deck: RandomCoffeePreviewDeck;
  label: string;
  ring: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        "group relative block cursor-pointer self-center text-left outline-none [--card:150px] sm:[--card:184px] lg:[--card:200px]",
        "ml-[calc(var(--card)*0.2)] h-[calc(var(--card)*16/9+var(--card)*0.24)] w-[calc(var(--card)*1.68)]",
      )}
    >
      <span aria-hidden="true" className="absolute inset-0">
        {FAN.map(({ face, left, top, rotate }) => (
          <span
            key={face}
            className={cn(
              "absolute block w-[var(--card)] origin-bottom overflow-hidden rounded-[10px] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.65)] ring-1 ring-black/40 transition-transform duration-300 ease-out",
              face === "cover" && "motion-safe:group-hover:-translate-y-1.5",
            )}
            style={{
              left: `calc(var(--card) * ${left})`,
              top: `calc(var(--card) * ${top})`,
              rotate: `${rotate}deg`,
            }}
          >
            <ProfileCard invite={invite} deck={deck} face={face} />
          </span>
        ))}
      </span>

      <span
        aria-hidden="true"
        className="absolute top-[calc(var(--card)*16/9-var(--card)*0.42)] left-[calc(var(--card)*-0.2)] size-[calc(var(--card)*0.46)]"
      >
        {ring}
      </span>

      <span className="absolute bottom-0 left-1/2 inline-flex h-11 max-w-[calc(100vw-48px)] -translate-x-1/2 items-center gap-2.5 rounded-full bg-white py-1.5 pr-5 pl-1.5 text-[15px] font-semibold whitespace-nowrap text-zinc-950 shadow-[0_12px_30px_-10px_rgba(0,0,0,0.7)] transition-colors group-hover:bg-zinc-200 group-focus-visible:ring-2 group-focus-visible:ring-white group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-[#151518] sm:left-[calc(var(--card)*0.3)] sm:max-w-none sm:translate-x-0">
        <span className="inline-flex size-8 items-center justify-center rounded-full bg-zinc-950 text-white">
          <Play className="size-3.5 translate-x-px fill-current" aria-hidden="true" />
        </span>
        <span className="min-w-0 truncate">{label}</span>
      </span>
    </button>
  );
}

/** The partner's photo inside a ring of their four functions, strongest first. */
function PartnerRing({
  name,
  functions,
  surface = "dark",
  className,
}: {
  name: string;
  functions: FunctionCode[];
  surface?: "dark" | "light";
  className?: string;
}) {
  const light = surface === "light";
  const person = getSixPillarsPreviewPerson(name);
  const stops = functions.map((code, index) => {
    const start = index * 90;
    const color = light ? MANUAL_FUNCTION_TOKEN_META[code].color : FUNCTIONS[code].onDark;

    return `${color} ${start + 3}deg ${start + 87}deg, transparent ${start + 87}deg ${start + 93}deg`;
  });

  return (
    <span
      aria-hidden="true"
      className={cn("block flex-none rounded-full", light ? "p-[3px]" : "p-[4px]", className)}
      style={{ background: `conic-gradient(transparent 0deg 3deg, ${stops.join(", ")})` }}
    >
      <span
        className={cn(
          "block size-full rounded-full",
          light ? "bg-background p-[3px]" : "bg-[#121214] p-[4px]",
        )}
      >
        <span className="relative block size-full overflow-hidden rounded-full bg-zinc-800">
          {person ? (
            <Image src={person.avatarSrc} alt="" fill sizes="96px" className="object-cover" />
          ) : null}
        </span>
      </span>
    </span>
  );
}

type ProfileCardFace = "cover" | "do" | "dont";

/** Card sizes are drawn at 432 px wide and scale with the card through container units. */
const u = (px: number) => `${((px / 432) * 100).toFixed(3)}cqw`;

const MONO = "ui-monospace, 'SFMono-Regular', Menlo, Monaco, Consolas, monospace";
const SYMBOLS =
  "'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif";
const PAGE: Record<ProfileCardFace, number> = { cover: 1, do: 2, dont: 3 };

/** One of the 30-second cards (cooperly_app features/profile-cards/profile-card.tsx). */
function ProfileCard({
  invite,
  deck,
  face,
}: {
  invite: RandomCoffeePreviewInvite;
  deck: RandomCoffeePreviewDeck;
  face: ProfileCardFace;
}) {
  const dark = face === "dont";
  const page = PAGE[face];
  const profileName = invite.profileLabel.split(" - ")[0];
  const fileLabel = invite.partnerName.toLowerCase().replace(/\s+/g, "-");
  const containerStyle: React.CSSProperties = {
    containerType: "inline-size",
    aspectRatio: "9 / 16",
  };

  if (face === "cover") {
    return (
      <span className="flex w-full flex-col overflow-hidden bg-white text-zinc-950" style={containerStyle}>
        <CardWindowBar dark={false} fileLabel={fileLabel} page={page} />
        <span className="flex min-h-0 flex-1 flex-col" style={{ padding: `${u(22)} ${u(26)} ${u(20)}` }}>
          <span className="block" style={{ fontSize: u(15), lineHeight: 1.3 }}>
            <span className="font-semibold">{invite.partnerName}</span>{" "}
            <span className="text-zinc-950/60">· {profileName}</span>
          </span>
          <span
            className="flex flex-none items-center"
            style={{ marginTop: u(18), width: u(104), height: u(88), fontFamily: SYMBOLS, fontSize: u(76), lineHeight: 1 }}
          >
            {deck.glyph}
          </span>
          <span
            className="block font-medium text-balance break-words hyphens-auto"
            style={{ marginTop: u(16), fontSize: u(48), lineHeight: 0.98, letterSpacing: "-0.045em" }}
          >
            {deck.headline}
          </span>
          <span className="block text-zinc-950/65" style={{ marginTop: u(14), fontSize: u(18), lineHeight: 1.4 }}>
            {deck.reason}
          </span>
          <span className="flex-1" />
          <span className="grid grid-cols-2" style={{ gap: `${u(14)} ${u(18)}` }}>
            {profileFunctions(invite.partnerName).map((code) => {
              const color = MANUAL_FUNCTION_TOKEN_META[code].color;

              return (
                <span key={code} className="block min-w-0">
                  <span className="block rounded-full" style={{ height: u(4), background: color }} />
                  <span className="flex items-baseline" style={{ marginTop: u(9), gap: u(6) }}>
                    <span className="font-semibold" style={{ fontSize: u(22), lineHeight: 1, color }}>
                      {code}
                    </span>
                    <span
                      className="truncate text-zinc-950/60 uppercase"
                      style={{ fontFamily: MONO, fontSize: u(9.5), letterSpacing: "0.06em" }}
                    >
                      {FUNCTIONS[code].label}
                    </span>
                  </span>
                  <span
                    className="block font-medium break-words hyphens-auto"
                    style={{ marginTop: u(6), fontSize: u(14), lineHeight: 1.25 }}
                  >
                    {deck.functionLines[code]}
                  </span>
                </span>
              );
            })}
          </span>
          <span
            className="flex items-center justify-between text-zinc-950/60"
            style={{
              marginTop: u(22),
              paddingTop: u(14),
              borderTop: "1px solid rgba(9,9,11,0.09)",
              fontFamily: MONO,
              fontSize: u(11),
            }}
          >
            <span>cooperly.ai</span>
            <span className="flex items-center" style={{ gap: u(10) }}>
              Do · Don’t →
              <CardPager dark={false} page={page} />
            </span>
          </span>
        </span>
      </span>
    );
  }

  const heading = dark ? "Don’t" : "Do";
  const items = dark ? deck.dont : deck.do;

  return (
    <span
      className={cn(
        "flex w-full flex-col overflow-hidden",
        dark ? "bg-zinc-950 text-zinc-50" : "bg-white text-zinc-950",
      )}
      style={containerStyle}
    >
      <CardWindowBar dark={dark} fileLabel={fileLabel} page={page} />
      <span className="flex min-h-0 flex-1 flex-col" style={{ padding: `${u(24)} ${u(28)} ${u(22)}` }}>
        <span className="block" style={{ fontSize: u(15), lineHeight: 1.3 }}>
          <span style={{ marginRight: u(8), fontFamily: SYMBOLS, fontSize: u(16), lineHeight: 1 }}>
            {deck.glyph}
          </span>
          <span className="font-semibold">{invite.partnerName}</span>{" "}
          <span className={dark ? "text-white/60" : "text-zinc-950/60"}>· {profileName}</span>
        </span>
        <span
          className="block font-medium"
          style={{
            marginTop: u(40),
            fontSize: heading.length > 5 ? u(80) : u(112),
            lineHeight: heading.length > 5 ? 0.9 : 0.82,
            letterSpacing: "-0.055em",
          }}
        >
          {heading}
        </span>
        <span
          className={cn("block uppercase", dark ? "text-white/60" : "text-zinc-950/60")}
          style={{ marginTop: u(16), fontFamily: MONO, fontSize: u(10.5), letterSpacing: "0.12em" }}
        >
          {dark ? "What gets in the way" : "What helps"}
        </span>
        <span className="block" style={{ marginTop: u(18) }}>
          {items.map((item) => (
            <span
              key={item.title}
              className="grid"
              style={{
                gridTemplateColumns: `${u(28)} minmax(0, 1fr)`,
                gap: u(14),
                padding: `${u(16)} 0`,
                borderTop: `1px solid ${dark ? "rgba(250,250,250,0.12)" : "rgba(9,9,11,0.09)"}`,
              }}
            >
              <span
                className={cn(
                  "flex items-center justify-center rounded-full border",
                  dark ? "border-white/20" : "border-zinc-950/15",
                )}
                style={{ width: u(28), height: u(28) }}
              >
                <svg
                  width="50%"
                  height="50%"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {dark ? (
                    <>
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </>
                  ) : (
                    <path d="M20 6 9 17l-5-5" />
                  )}
                </svg>
              </span>
              <span className="min-w-0">
                <span
                  className="block font-semibold"
                  style={{ fontSize: u(20), lineHeight: 1.25, letterSpacing: "-0.02em" }}
                >
                  {item.title}
                </span>
                <span
                  className={cn("block", dark ? "text-white/65" : "text-zinc-950/65")}
                  style={{ marginTop: u(5), fontSize: u(15), lineHeight: 1.45 }}
                >
                  {item.detail}
                </span>
              </span>
            </span>
          ))}
        </span>
        <span className="flex-1" />
        <span
          className={cn("flex items-center justify-between", dark ? "text-white/55" : "text-zinc-950/60")}
          style={{ fontFamily: MONO, fontSize: u(11) }}
        >
          <span>cooperly.ai</span>
          <CardPager dark={dark} page={page} />
        </span>
      </span>
    </span>
  );
}

function CardWindowBar({
  dark,
  fileLabel,
  page,
}: {
  dark: boolean;
  fileLabel: string;
  page: number;
}) {
  return (
    <span
      className={cn(
        "grid flex-none items-center",
        dark ? "bg-zinc-900 text-white/55" : "bg-[#F4F1E8] text-zinc-950/60",
      )}
      style={{
        height: u(40),
        padding: `0 ${u(16)}`,
        gridTemplateColumns: `${u(60)} minmax(0, 1fr) ${u(60)}`,
        borderBottom: `1px solid ${dark ? "rgba(250,250,250,0.08)" : "rgba(9,9,11,0.09)"}`,
        fontFamily: MONO,
        fontSize: u(11),
      }}
    >
      <span className="flex" style={{ gap: u(5) }}>
        {[0, 1, 2].map((dot) => (
          <span
            key={dot}
            className={cn("rounded-full", dark ? "bg-white/20" : "bg-zinc-950/15")}
            style={{ width: u(7), height: u(7) }}
          />
        ))}
      </span>
      <span className="truncate text-center">{fileLabel}</span>
      <span className="text-right">{page} / 3</span>
    </span>
  );
}

function CardPager({ dark, page }: { dark: boolean; page: number }) {
  return (
    <span className="flex" style={{ gap: u(5) }}>
      {[1, 2, 3].map((dot) => (
        <span
          key={dot}
          className={cn(
            "rounded-full",
            dot === page
              ? dark
                ? "bg-white"
                : "bg-zinc-950"
              : dark
                ? "bg-white/20"
                : "bg-zinc-950/15",
          )}
          style={{ width: u(6), height: u(6) }}
        />
      ))}
    </span>
  );
}

const TOPIC_TONES = {
  work: "border-sky-200 bg-sky-50 text-sky-700",
  life: "border-emerald-200 bg-emerald-50 text-emerald-700",
} as const;

function StartTopics({
  guide,
  workTopics,
  lifestyleTopics,
}: {
  guide: string;
  workTopics: readonly string[];
  lifestyleTopics: readonly string[];
}) {
  const headingId = React.useId();

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <h2 id={headingId} className="m-0 text-[26px] leading-tight font-semibold tracking-[-0.02em]">
          Where to start
        </h2>
        <p className="m-0 max-w-[70ch] text-[17px] leading-[1.6] text-pretty text-foreground/80">
          {guide}
        </p>
      </div>
      <div className="grid gap-3.5 md:grid-cols-2">
        <TopicGroup tone="work" label="About work" icon={BriefcaseBusiness} topics={workTopics} />
        <TopicGroup tone="life" label="Not about work" icon={Coffee} topics={lifestyleTopics} />
      </div>
    </section>
  );
}

function TopicGroup({
  tone,
  label,
  icon: Icon,
  topics,
}: {
  tone: keyof typeof TOPIC_TONES;
  label: string;
  icon: LucideIcon;
  topics: readonly string[];
}) {
  const headingId = React.useId();

  return (
    <section aria-labelledby={headingId} className="rounded-[14px] border border-border bg-muted/70 p-3">
      <header className="flex items-center gap-3 px-1.5 pt-1 pb-3">
        <span
          className={cn(
            "inline-flex size-8 flex-none items-center justify-center rounded-full border",
            TOPIC_TONES[tone],
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <h3 id={headingId} className="m-0 text-base font-semibold">
          {label}
        </h3>
        <span aria-hidden="true" className="ml-auto text-lg font-semibold tabular-nums">
          {topics.length}
        </span>
      </header>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {topics.map((topic) => (
          <li
            key={topic}
            className="rounded-[12px] border border-border bg-background px-4 py-3.5 text-[15px] leading-snug font-medium"
          >
            {topic}
          </li>
        ))}
      </ul>
    </section>
  );
}

function PreviousCoffees({ invites }: { invites: readonly RandomCoffeePreviewInvite[] }) {
  const headingId = React.useId();
  const teams = Array.from(new Set(invites.map((invite) => invite.teamName)));

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        <h2 id={headingId} className="m-0 text-[26px] leading-tight font-semibold tracking-[-0.02em]">
          Previous coffees{" "}
          <span className="font-medium text-muted-foreground tabular-nums">{invites.length}</span>
        </h2>
        <p className="m-0 text-sm text-muted-foreground">People from {teams.join(", ")}.</p>
      </div>
      <ul className="site-scrollbar-auto m-0 -mx-1 flex snap-x snap-mandatory scroll-px-1 list-none gap-3 overflow-x-auto p-0 px-1 pb-3">
        {invites.map((invite) => (
          <li
            key={invite.id}
            className="relative flex w-[min(320px,calc(100vw-64px))] flex-none snap-start flex-col rounded-[14px] border border-border bg-background p-4"
          >
            <div className="flex flex-1 items-start gap-3">
              <PartnerRing
                name={invite.partnerName}
                functions={profileFunctions(invite.partnerName)}
                surface="light"
                className="size-12"
              />
              <div className="min-w-0">
                <p className="m-0 line-clamp-2 text-[15px] leading-snug font-semibold break-words">
                  {invite.partnerName}
                </p>
                <p className="m-0 truncate text-[13px] text-muted-foreground">{invite.partnerRole}</p>
                <p className="m-0 truncate text-[13px] text-muted-foreground">{invite.teamName}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
              <span className="min-w-0 truncate text-xs text-muted-foreground">
                {formatShortDate(invite.weekStart)} · {invite.format}
              </span>
              <span className="inline-flex flex-none items-center gap-1 text-sm font-semibold">
                Profile
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
